import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Form,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getHomework } from "../../store/slices/homework";
import { getClassSubject } from "../../store/slices/subject";
import toast from "react-hot-toast";

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function StudentHomework() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const activeStudent = useSelector(
    (state) => state.activestudent?.student
  );


  const activeStandardId = activeStudent?.standardId?._id;
  const activeSectionId = activeStudent?.sectionId?._id;

  const [data, setData] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [itemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);


  const fetchSubjects = useCallback(() => {
    if (!activeStandardId) return;

    dispatch(getClassSubject({ standardId: activeStandardId }))
      .unwrap()
      .then((res) => {
        setSubjects(res?.data?.[0]?.subjectId || []);
      })
      .catch(() => toast.error("Failed to load subjects"));
  }, [dispatch, activeStandardId]);

  const fetchHomework = useCallback(
    (subjectId = "") => {
      if (!activeStandardId || !activeSectionId) return;

      setIsLoading(true);

      dispatch(
        getHomework({
          standardId: activeStandardId,
          sectionId: activeSectionId,
          ...(subjectId && { subjectId }),
        })
      )
        .unwrap()
        .then((res) => {
          setData(res?.data || []);
          setTotalData(res?.totalDataCount || 0);
        })
        .catch(() => {
          toast.error("Failed to fetch homework list");
          setData([]);
        })
        .finally(() => setIsLoading(false));
    },
    [dispatch, activeStandardId, activeSectionId]
  );


  useEffect(() => {
    fetchSubjects();
    fetchHomework();
  }, [fetchSubjects, fetchHomework]);

  useEffect(() => {
    setCurrentPage(1);
    fetchHomework(selectedSubject);
  }, [selectedSubject, fetchHomework]);

  const totalPages = Math.ceil(totalData / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = data.slice(startIndex, startIndex + itemsPerPage);

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/student/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>


            <FiChevronRight className="separator" />

            <span className="active">Homework</span>
          </div>

        </div>
      </div>
      <div className="staff-card">
        <Row className="align-items-center mb-3">


          <Col md={3} className="ms-auto">
            <Form.Select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="">All Subjects</option>
              {subjects.map((sub) => (
                <option key={sub._id} value={sub._id}>
                  {sub.name}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {/* Table */}
        <div className="table-responsive">
          <Table className="staff-table align-middle">
            <thead>
              <tr>

                <th className="text-center">#</th>
                <th className="text-center">Class-Sec</th>
                <th className="text-center">Subject</th>
                <th className="text-center">Assign Date</th>
                <th className="text-center">Due Date</th>
                <th className="text-center">Description</th>
                <th className="text-center">File</th>

              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="text-center py-3">
                    Loading...
                  </td>
                </tr>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((item, i) => (
                  <tr key={item._id}>

                    <td className="text-center"> {startIndex + i + 1}</td>
                    <td className="text-center">
                      {item.standardId?.name}-{item.sectionId?.name}
                    </td>
                    <td className="text-center">{item.subjectId?.name}</td>

                    <td className="text-center">{formatDate(item.assignDate)}</td>
                    <td className="text-center">{formatDate(item.dueDate)}</td>
                    <td className="text-center" style={{ maxWidth: "200px" }}>
                      {item.description}
                    </td>
                    <td className="text-center">
                      {item.file ? (
                        <a href={item.file} target="_blank" rel="noreferrer">
                          View
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-3">
                    No homework list found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-end my-3">
          <Pagination className="mb-0">

            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            />

            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;

              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              }

              // Left dots
              if (page === currentPage - 2) {
                return <Pagination.Ellipsis key={`left-${page}`} disabled />;
              }

              // Right dots
              if (page === currentPage + 2) {
                return <Pagination.Ellipsis key={`right-${page}`} disabled />;
              }

              return null;
            })}

            <Pagination.Next
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
            />

          </Pagination>
        </div>
      </div>
    </>
  );
}